import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { AppShell } from '@/components/Layout/AppShell';
import { HowMuchIsTheFish } from '@/components/molecules/HowMuchIsTheFish';
import { NotFound } from '@/components/molecules/NotFound';
import * as features from '@/config/features';
import { useAuth, usePermissions } from '@/contexts/auth';
import { DEMO_MODE_KEY, useDemoMode } from '@/contexts/demo/DemoContext';
import RegionLiceReport from '@/routes/Client/Overview/RegionLiceReport';
import RegionOverviewContainer from '@/routes/Client/Overview/RegionOverview';
import UserManagementView from '@/routes/shared/UserManagementPage';
import { parsePositiveInt } from '@/utils/parsePositiveInt';

import HelpPageContainer from './components/Help/HelpPageContainer';
import ImpersonationBanner from './components/ImpersonationBanner/ImpersonationBanner';
import { useSwitchBackToOwnAccount } from './components/ImpersonationBanner/useSwitchBackToOwnAccount';
import { LocationRoutes } from './Location/LocationRoutes';
import { MoveBioscopeContainer } from './MoveBioScope/MoveBioscopeContainer';

/** `/c/:clientId/cages/:cageId`: the oldest pen URLs, passed through the `cages/v2` redirect. */
function LegacyCageRedirect(props: { regionUrl: string }) {
    const { cageId } = useParams();
    const { search } = useLocation();
    return <Navigate to={`${props.regionUrl}/cages/v2/${cageId}${search}`} replace />;
}

export default function Client() {
    const { clientId: clientParam } = useParams();
    // The URL as typed, so redirects keep it; the id itself is parsed below.
    const baseUrl = `/c/${clientParam}`;

    const handleSwitchBackToOwnAccount = useSwitchBackToOwnAccount();
    const { clients, user } = useAuth();
    const { hasUserFeature, isOptoScaleAdmin } = usePermissions();
    const { isDemoMode, toggleDemoMode } = useDemoMode();
    const { t } = useTranslation();

    const clientId = parsePositiveInt(clientParam);
    const client = clients.find((client) => client.id === clientId);

    // Handler to disable demo mode
    const handleDisableDemoMode = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault();
        // Update localStorage directly before reload to ensure it's saved
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem(DEMO_MODE_KEY, 'false');
            } catch {
                // Ignore storage errors
            }
        }
        toggleDemoMode();
        window.location.reload();
    };

    if (!client) {
        return <NotFound />;
    }

    const isImpersonator = Boolean(user?.impersonatorUser?.id);
    const impersonationBanner = isImpersonator ? (
        <ImpersonationBanner
            impersonatorName={`${user?.impersonatorUser?.firstName ?? ''} ${user?.impersonatorUser?.lastName ?? ''}`.trim()}
            impersonatedName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
            onSwitchBack={handleSwitchBackToOwnAccount}
        />
    ) : undefined;

    const demoModeBanner =
        isDemoMode && isOptoScaleAdmin ? (
            <ImpersonationBanner
                message={t('layout.clientRoutes.demoModeMessage')}
                buttonText={t('layout.clientRoutes.disableDemoMode')}
                onSwitchBack={handleDisableDemoMode}
            />
        ) : undefined;

    return (
        <>
            <AppShell topBanner={impersonationBanner || demoModeBanner}>
                <Routes>
                    <Route
                        path="location/:locationId/*"
                        element={<LocationRoutes clientId={client.id} regionUrl={baseUrl} />}
                    />

                    <Route path="help" element={<HelpPageContainer />} />
                    <Route path="lice-report" element={<RegionLiceReport clientId={client.id} />} />
                    <Route path="users" element={<UserManagementView clientId={client.id} />} />
                    <Route
                        path="change-pen"
                        element={<MoveBioscopeContainer clientId={client.id} />}
                    />

                    {/* Legacy cage URL redirects */}
                    <Route path="cages" element={<Navigate to={baseUrl} replace />} />
                    <Route path="cages/v2" element={<Navigate to={`${baseUrl}/`} replace />} />
                    <Route path="cages/alternative" element={<Navigate to={baseUrl} replace />} />
                    {['v1', 'v2', 'v3', 'v3-new', 'alternative'].map((version) => (
                        <Route
                            key={version}
                            path={`cages/${version}/:cageId/*`}
                            element={<Navigate to={baseUrl} replace />}
                        />
                    ))}
                    <Route
                        path="cages/:cageId/*"
                        element={<LegacyCageRedirect regionUrl={baseUrl} />}
                    />

                    {/* Main region overview. Legacy /cages/v3 lands here through the cages/:cageId redirect above. */}
                    <Route index element={<RegionOverviewContainer clientId={client.id} />} />

                    <Route path="*" element={<NotFound />} />
                </Routes>
            </AppShell>

            {hasUserFeature(features.HOW_MUCH_IS_THE_FISH) && <HowMuchIsTheFish />}
        </>
    );
}
